import * as React from 'react';

import { create } from '@bufbuild/protobuf';
import {
  SnapshotOverlayAnchor,
  type SnapshotOverlayLayer,
  SnapshotOverlayLayerSchema,
} from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/camera_local_api_pb';
import {
  Button,
  Col,
  Row,
  Select,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import {
  LucideArrowDown,
  LucideArrowUp,
  LucideCamera,
  LucideDownload,
  LucideImage,
  LucidePlus,
  LucideTrash2,
  LucideType,
  LucideX,
} from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import type { useCameraLocalClient } from '~/shared/providers/libp2p/useCameraLocalClient';

type SnapshotEntry = {
  channel: number;
  dataUrl: string;
  blob: Blob;
  fileId: string;
};

export type SnapshotTextLayerDraft = {
  id: string;
  kind: 'text';
  anchor: SnapshotOverlayAnchor;
  text: string;
  fontSizePx: string;
  color: string;
};

export type SnapshotImageLayerDraft = {
  id: string;
  kind: 'image';
  anchor: SnapshotOverlayAnchor;
  dataUrl: string;
};

export type SnapshotLayerDraft =
  | SnapshotTextLayerDraft
  | SnapshotImageLayerDraft;

type UseCameraSnapshotParams = {
  cameraId: string;
  cameraClient: ReturnType<typeof useCameraLocalClient>;
  layers?: SnapshotLayerDraft[];
};

const SUPPORTED_IMAGE_CONTENT_TYPES = new Set(['image/png', 'image/jpeg']);

class SnapshotLayerSerializationError extends Error {
  constructor(
    readonly code:
      | 'empty-text'
      | 'invalid-image-data-url'
      | 'unsupported-image-type',
  ) {
    super(code);
  }
}

export function createDefaultTextLayerDraft(): SnapshotTextLayerDraft {
  return {
    id: createDraftID(),
    kind: 'text',
    anchor: SnapshotOverlayAnchor.TOP_LEFT,
    text: '',
    fontSizePx: '24',
    color: '#ffffff',
  };
}

export function createDefaultImageLayerDraft(): SnapshotImageLayerDraft {
  return {
    id: createDraftID(),
    kind: 'image',
    anchor: SnapshotOverlayAnchor.TOP_LEFT,
    dataUrl: '',
  };
}

export function serializeSnapshotLayers(
  layers: SnapshotLayerDraft[],
): SnapshotOverlayLayer[] {
  return layers.map((layer) => {
    if (layer.kind === 'text') {
      const text = layer.text.trim();
      if (text.length === 0) {
        throw new SnapshotLayerSerializationError('empty-text');
      }
      const fontSize = Number.parseInt(layer.fontSizePx, 10);
      return create(SnapshotOverlayLayerSchema, {
        layer: {
          case: 'text',
          value: {
            text,
            anchor: normalizeTextAnchor(layer.anchor),
            fontSizePx:
              Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 24,
            color: layer.color.trim() || '#ffffff',
          },
        },
      });
    }

    const { contentType, bytes } = parseImageDataUrl(layer.dataUrl);
    return create(SnapshotOverlayLayerSchema, {
      layer: {
        case: 'image',
        value: {
          imageBytes: bytes,
          contentType,
          anchor: layer.anchor,
        },
      },
    });
  });
}

function parseImageDataUrl(dataUrl: string): {
  contentType: string;
  bytes: Uint8Array;
} {
  const match = dataUrl
    .trim()
    .match(/^data:([^;,]+);base64,([a-zA-Z0-9+/=\s]+)$/);
  if (!match) {
    throw new SnapshotLayerSerializationError('invalid-image-data-url');
  }

  const contentType = match[1].toLowerCase();
  if (!SUPPORTED_IMAGE_CONTENT_TYPES.has(contentType)) {
    throw new SnapshotLayerSerializationError('unsupported-image-type');
  }

  const base64Data = match[2].replace(/\s+/g, '');
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return { contentType, bytes };
}

function normalizeTextAnchor(
  anchor: SnapshotOverlayAnchor,
): SnapshotOverlayAnchor {
  return anchor === SnapshotOverlayAnchor.FULL_FRAME
    ? SnapshotOverlayAnchor.TOP_LEFT
    : anchor;
}

function createDraftID(): string {
  return `snapshot-layer-${Math.random().toString(36).slice(2, 10)}`;
}

export function useCameraSnapshot({
  cameraId,
  cameraClient,
  layers,
}: UseCameraSnapshotParams) {
  const t = useTranslations();

  const [isCapturing, setIsCapturing] = React.useState(false);
  const [snapshots, setSnapshots] = React.useState<SnapshotEntry[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const snapshotsRef = React.useRef<SnapshotEntry[]>([]);

  React.useEffect(() => {
    snapshotsRef.current = snapshots;
  }, [snapshots]);

  const handleCapture = React.useCallback(async () => {
    if (!cameraClient) return;
    setIsCapturing(true);
    setError(null);

    try {
      const response = await cameraClient.takeSnapshot({
        cameraId,
        layers: serializeSnapshotLayers(layers ?? []),
      });

      const entries: SnapshotEntry[] = response.snapshots.map((snap) => {
        const blob = new Blob([snap.jpegData as BlobPart], {
          type: snap.contentType || 'image/jpeg',
        });
        const dataUrl = URL.createObjectURL(blob);
        return {
          channel: snap.channel,
          dataUrl,
          blob,
          fileId: snap.fileId,
        };
      });

      setSnapshots((prev) => {
        prev.forEach((s) => URL.revokeObjectURL(s.dataUrl));
        return entries;
      });
    } catch (err) {
      setError(snapshotErrorMessage(err, t));
    } finally {
      setIsCapturing(false);
    }
  }, [cameraClient, cameraId, layers, t]);

  const handleDownload = React.useCallback(
    (entry: SnapshotEntry) => {
      const url = URL.createObjectURL(entry.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snapshot-${cameraId}-ch${entry.channel}-${Date.now()}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [cameraId],
  );

  const handleDismiss = React.useCallback(() => {
    setSnapshots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.dataUrl));
      return [];
    });
    setError(null);
  }, []);

  React.useEffect(
    () => () => {
      snapshotsRef.current.forEach((s) => URL.revokeObjectURL(s.dataUrl));
    },

    [],
  );

  return {
    isCapturing,
    snapshots,
    error,
    handleCapture,
    handleDownload,
    handleDismiss,
  };
}

function snapshotErrorMessage(
  error: unknown,
  t: ReturnType<typeof useTranslations>,
): string {
  if (error instanceof SnapshotLayerSerializationError) {
    switch (error.code) {
      case 'empty-text':
        return t.hardwareView.cameraDetailView.snapshot.errors.emptyTextLayer;
      case 'invalid-image-data-url':
        return t.hardwareView.cameraDetailView.snapshot.errors
          .invalidImageDataUrl;
      case 'unsupported-image-type':
        return t.hardwareView.cameraDetailView.snapshot.errors
          .unsupportedImageType;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return t.hardwareView.cameraDetailView.snapshot.captureErrorFallback;
}

type CameraSnapshotButtonProps = {
  isCapturing: boolean;
  onCapture: () => void;
};

export const CameraSnapshotButton: React.FC<CameraSnapshotButtonProps> = ({
  isCapturing,
  onCapture,
}) => {
  const t = useTranslations();

  return (
    <Button
      variant="outline"
      size="default"
      onPress={onCapture}
      isDisabled={isCapturing}
      className="@CameraSnapshotButton"
    >
      <LucideCamera className="size-4" />
      {isCapturing
        ? t.hardwareView.cameraDetailView.snapshot.capturing
        : t.hardwareView.cameraDetailView.snapshot.capture}
    </Button>
  );
};

type CameraSnapshotOverlayEditorProps = {
  layers: SnapshotLayerDraft[];
  onChange: (layers: SnapshotLayerDraft[]) => void;
};

export const CameraSnapshotOverlayEditor: React.FC<
  CameraSnapshotOverlayEditorProps
> = ({ layers, onChange }) => {
  const t = useTranslations();

  const textAnchorOptions = React.useMemo(
    () => [
      {
        key: String(SnapshotOverlayAnchor.TOP_LEFT),
        label: t.hardwareView.cameraDetailView.snapshot.anchors.topLeft,
      },
      {
        key: String(SnapshotOverlayAnchor.TOP_RIGHT),
        label: t.hardwareView.cameraDetailView.snapshot.anchors.topRight,
      },
      {
        key: String(SnapshotOverlayAnchor.BOTTOM_LEFT),
        label: t.hardwareView.cameraDetailView.snapshot.anchors.bottomLeft,
      },
      {
        key: String(SnapshotOverlayAnchor.BOTTOM_RIGHT),
        label: t.hardwareView.cameraDetailView.snapshot.anchors.bottomRight,
      },
    ],
    [t],
  );

  const imageAnchorOptions = React.useMemo(
    () => [
      ...textAnchorOptions,
      {
        key: String(SnapshotOverlayAnchor.FULL_FRAME),
        label: t.hardwareView.cameraDetailView.snapshot.anchors.fullFrame,
      },
    ],
    [t, textAnchorOptions],
  );

  const layerTypeOptions = React.useMemo(
    () => [
      {
        key: 'text',
        label: t.hardwareView.cameraDetailView.snapshot.layerTypes.text,
      },
      {
        key: 'image',
        label: t.hardwareView.cameraDetailView.snapshot.layerTypes.image,
      },
    ],
    [t],
  );

  const setLayer = React.useCallback(
    (index: number, nextLayer: SnapshotLayerDraft) => {
      onChange(
        layers.map((layer, layerIndex) =>
          layerIndex === index ? nextLayer : layer,
        ),
      );
    },
    [layers, onChange],
  );

  const removeLayer = React.useCallback(
    (index: number) => {
      onChange(layers.filter((_, layerIndex) => layerIndex !== index));
    },
    [layers, onChange],
  );

  const moveLayer = React.useCallback(
    (index: number, direction: -1 | 1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= layers.length) return;
      const nextLayers = [...layers];
      const [current] = nextLayers.splice(index, 1);
      nextLayers.splice(targetIndex, 0, current);
      onChange(nextLayers);
    },
    [layers, onChange],
  );

  const updateLayerType = React.useCallback(
    (index: number, nextKind: string | undefined) => {
      if (nextKind === 'image') {
        const previous = layers[index];
        const anchor =
          previous?.kind === 'image'
            ? previous.anchor
            : SnapshotOverlayAnchor.TOP_LEFT;
        setLayer(index, {
          ...createDefaultImageLayerDraft(),
          id: previous?.id ?? createDraftID(),
          anchor,
        });
        return;
      }
      const previous = layers[index];
      const anchor =
        previous?.anchor === SnapshotOverlayAnchor.FULL_FRAME
          ? SnapshotOverlayAnchor.TOP_LEFT
          : (previous?.anchor ?? SnapshotOverlayAnchor.TOP_LEFT);
      setLayer(index, {
        ...createDefaultTextLayerDraft(),
        id: previous?.id ?? createDraftID(),
        anchor,
      });
    },
    [layers, setLayer],
  );

  const handleUploadImage = React.useCallback(
    async (index: number, file: File | null) => {
      if (!file) return;
      const dataUrl = await readFileAsDataURL(file);
      const layer = layers[index];
      if (!layer || layer.kind !== 'image') return;
      setLayer(index, { ...layer, dataUrl });
    },
    [layers, setLayer],
  );

  return (
    <Col
      gap3
      className="@CameraSnapshotOverlayEditor rounded-lg border border-neutral/15 p-3"
    >
      <Row className="items-center justify-between gap-2">
        <div className="text-sm font-medium text-neutral">
          {t.hardwareView.cameraDetailView.snapshot.overlayEditorTitle}
        </div>
        <Row gap2 className="flex-wrap">
          <Button
            variant="outline"
            size="small"
            onPress={() => onChange([...layers, createDefaultTextLayerDraft()])}
          >
            <LucidePlus className="size-4" />
            <LucideType className="size-4" />
            {t.hardwareView.cameraDetailView.snapshot.addTextLayer}
          </Button>
          <Button
            variant="outline"
            size="small"
            onPress={() =>
              onChange([...layers, createDefaultImageLayerDraft()])
            }
          >
            <LucidePlus className="size-4" />
            <LucideImage className="size-4" />
            {t.hardwareView.cameraDetailView.snapshot.addImageLayer}
          </Button>
        </Row>
      </Row>

      {layers.length === 0 && (
        <div className="text-sm text-neutral/60">
          {t.hardwareView.cameraDetailView.snapshot.noLayers}
        </div>
      )}

      {layers.map((layer, index) => (
        <Col
          key={layer.id}
          gap3
          className="rounded-lg border border-neutral/10 bg-neutral/5 p-3"
        >
          <Row className="flex-wrap items-center gap-2">
            <Select
              aria-label={
                t.hardwareView.cameraDetailView.snapshot.layerTypeLabel
              }
              label={t.hardwareView.cameraDetailView.snapshot.layerTypeLabel}
              value={layer.kind}
              onChange={(value) => updateLayerType(index, value)}
              options={layerTypeOptions}
              className="min-w-[150px]"
            />
            <Select
              aria-label={t.hardwareView.cameraDetailView.snapshot.anchorLabel}
              label={t.hardwareView.cameraDetailView.snapshot.anchorLabel}
              value={String(layer.anchor)}
              onChange={(value) => {
                const anchor = Number(value) as SnapshotOverlayAnchor;
                setLayer(index, {
                  ...layer,
                  anchor:
                    layer.kind === 'text' &&
                    anchor === SnapshotOverlayAnchor.FULL_FRAME
                      ? SnapshotOverlayAnchor.TOP_LEFT
                      : anchor,
                });
              }}
              options={
                layer.kind === 'text' ? textAnchorOptions : imageAnchorOptions
              }
              className="min-w-[170px]"
            />
            <Row gap2 className="ml-auto items-center">
              <Button
                variant="outline"
                size="small"
                onPress={() => moveLayer(index, -1)}
                isDisabled={index === 0}
                aria-label={
                  t.hardwareView.cameraDetailView.snapshot.moveLayerUp
                }
              >
                <LucideArrowUp className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="small"
                onPress={() => moveLayer(index, 1)}
                isDisabled={index === layers.length - 1}
                aria-label={
                  t.hardwareView.cameraDetailView.snapshot.moveLayerDown
                }
              >
                <LucideArrowDown className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="small"
                onPress={() => removeLayer(index)}
              >
                <LucideTrash2 className="size-4" />
                {t.hardwareView.cameraDetailView.snapshot.removeLayer}
              </Button>
            </Row>
          </Row>

          {layer.kind === 'text' ? (
            <div className="grid gap-3 md:grid-cols-3">
              <TextField
                label={t.hardwareView.cameraDetailView.snapshot.textLabel}
                value={layer.text}
                onChange={(value) =>
                  setLayer(index, {
                    ...layer,
                    text: String(value),
                  })
                }
              />
              <TextField
                label={t.hardwareView.cameraDetailView.snapshot.fontSizeLabel}
                value={layer.fontSizePx}
                onChange={(value) =>
                  setLayer(index, {
                    ...layer,
                    fontSizePx: String(value),
                  })
                }
                type="number"
              />
              <TextField
                label={t.hardwareView.cameraDetailView.snapshot.colorLabel}
                value={layer.color}
                onChange={(value) =>
                  setLayer(index, {
                    ...layer,
                    color: String(value),
                  })
                }
              />
            </div>
          ) : (
            <Col gap3>
              <Row className="flex-wrap items-end gap-3">
                <label className="flex min-w-[240px] flex-col gap-1 text-sm text-neutral">
                  <span>
                    {t.hardwareView.cameraDetailView.snapshot.uploadImageLabel}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(event) =>
                      handleUploadImage(index, event.target.files?.[0] ?? null)
                    }
                  />
                </label>
                <Button
                  variant="outline"
                  size="small"
                  onPress={() =>
                    setLayer(index, {
                      ...layer,
                      dataUrl: '',
                    })
                  }
                >
                  {t.hardwareView.cameraDetailView.snapshot.clearImage}
                </Button>
              </Row>
              <TextArea
                value={layer.dataUrl}
                onChange={(value) =>
                  setLayer(index, {
                    ...layer,
                    dataUrl: String(value),
                  })
                }
                className="w-full [&_textarea]:min-h-[120px]"
                label={
                  t.hardwareView.cameraDetailView.snapshot.imageDataUrlLabel
                }
                placeholder={
                  t.hardwareView.cameraDetailView.snapshot
                    .imageDataUrlPlaceholder
                }
              />
            </Col>
          )}
        </Col>
      ))}
    </Col>
  );
};

async function readFileAsDataURL(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () =>
      reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

type CameraSnapshotGalleryProps = {
  snapshots: SnapshotEntry[];
  error: string | null;
  onDownload: (entry: SnapshotEntry) => void;
  onDismiss: () => void;
};

export const CameraSnapshotGallery: React.FC<CameraSnapshotGalleryProps> = ({
  snapshots,
  error,
  onDownload,
  onDismiss,
}) => {
  const t = useTranslations();

  return (
    <Col gap2 className="@CameraSnapshotGallery">
      {error && <div className="text-sm text-failure">{error}</div>}

      {snapshots.length > 0 && (
        <Col gap2>
          <div
            className={
              snapshots.length > 1
                ? 'grid grid-cols-2 gap-2'
                : 'flex flex-col gap-2'
            }
          >
            {snapshots.map((entry) => (
              <Col
                key={entry.channel}
                gap2
                className="rounded-lg border border-neutral/20 p-2"
              >
                {snapshots.length > 1 && (
                  <div className="text-xs font-medium text-neutral/50">
                    {t.hardwareView.cameraDetailView.snapshot.channel(
                      entry.channel,
                    )}
                  </div>
                )}
                <img
                  src={entry.dataUrl}
                  alt={t.hardwareView.cameraDetailView.snapshot.imageAlt(
                    entry.channel,
                  )}
                  className="max-h-64 rounded object-contain"
                />
                <Row gap2>
                  <Button
                    variant="outline"
                    size="small"
                    onPress={() => onDownload(entry)}
                  >
                    <LucideDownload className="size-4" />
                    {t.hardwareView.cameraDetailView.snapshot.download}
                  </Button>
                </Row>
              </Col>
            ))}
          </div>
          <Row gap2>
            <Button size="default" onPress={onDismiss}>
              <LucideX className="size-4" />
              {t.hardwareView.cameraDetailView.snapshot.dismiss}
            </Button>
          </Row>
        </Col>
      )}
    </Col>
  );
};
